import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IRACING_AUTH_API_BASE } from "../utils/iracingAuthApi";
import { notifyIracingLinkChanged, notifyMemberConsentChanged } from "../utils/driverPrivacy";
import "../styles/AccountPage.css";

const AccountPage = ({ setIsLoggedIn }) => {
        const navigate = useNavigate();
        const [username, setUsername] = useState("");
        const [iracingLinked, setIracingLinked] = useState(false);
        const [iracingOnly, setIracingOnly] = useState(false);
        const [actionLoading, setActionLoading] = useState(false);
        const [message, setMessage] = useState({ type: "", text: "" });

        // iRacing-only -> link existing site account form state
        const [showMergeForm, setShowMergeForm] = useState(false);
        const [mergeUsername, setMergeUsername] = useState("");
        const [mergePassword, setMergePassword] = useState("");

        // Name-display consent (required by iRacing's terms before we may show
        // a member's display name or customer id anywhere on the site).
        const [consent, setConsent] = useState(null); // { linked, custId, displayName, consent, consentedAt }
        const [consentConfirm, setConsentConfirm] = useState(false);
        const [consentLoading, setConsentLoading] = useState(false);

        const loadConsent = useCallback(async () => {
                const token = localStorage.getItem("token");
                if (!token) return;
                try {
                        const response = await fetch(`${IRACING_AUTH_API_BASE}/api/member-consent`, {
                                headers: { Authorization: `Bearer ${token}` },
                        });
                        if (response.ok) setConsent(await response.json());
                } catch (err) {
                        console.error("member-consent load error:", err);
                }
        }, []);

        useEffect(() => {
                loadConsent();
        }, [loadConsent, iracingLinked]);

        const updateConsent = async (value) => {
                const token = localStorage.getItem("token");
                if (!token) {
                        navigate("/login");
                        return;
                }
                setConsentLoading(true);
                setMessage({ type: "", text: "" });
                try {
                        const response = await fetch(`${IRACING_AUTH_API_BASE}/api/member-consent`, {
                                method: "PUT",
                                headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                },
                                body: JSON.stringify({ consent: value }),
                        });
                        const data = await response.json();
                        if (response.ok) {
                                setConsent(data);
                                notifyMemberConsentChanged();
                                setConsentConfirm(false);
                                setMessage({
                                        type: "success",
                                        text: value
                                                ? "Your name will now be shown on iRacingData."
                                                : "Your name is now hidden. It may take up to a minute to disappear everywhere.",
                                });
                        } else {
                                setMessage({ type: "error", text: data.message || "Failed to update your choice." });
                        }
                } catch (err) {
                        console.error("member-consent update error:", err);
                        setMessage({ type: "error", text: "An error occurred. Please try again." });
                } finally {
                        setConsentLoading(false);
                }
        };

        useEffect(() => {
                const token = localStorage.getItem("token");
                if (!token) {
                        navigate("/login");
                        return;
                }

                setUsername(localStorage.getItem("username") || "");
                setIracingLinked(localStorage.getItem("iracingLinked") === "true");
                setIracingOnly(localStorage.getItem("iracingOnly") === "true");

                // Handle the ?iracing_linked=1 callback param from the backend.
                const urlParams = new URLSearchParams(window.location.search);
                if (urlParams.get("iracing_linked") === "1") {
                        localStorage.setItem("iracingLinked", "true");
                        notifyIracingLinkChanged();
                        setIracingLinked(true);
                        setMessage({ type: "success", text: "Your iRacing account has been linked." });
                        window.history.replaceState({}, document.title, "/account");
                }
        }, [navigate]);

        const handleLink = async () => {
                const token = localStorage.getItem("token");
                if (!token) {
                        navigate("/login");
                        return;
                }
                setActionLoading(true);
                setMessage({ type: "", text: "" });
                try {
                        const response = await fetch(`${IRACING_AUTH_API_BASE}/api/auth/iracing/link-init`, {
                                method: "POST",
                                headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                },
                        });
                        const data = await response.json();
                        if (response.ok && data.authorizeUrl) {
                                window.location.href = data.authorizeUrl;
                        } else {
                                setMessage({ type: "error", text: data.message || "Failed to start iRacing link." });
                                setActionLoading(false);
                        }
                } catch (err) {
                        console.error("link-init error:", err);
                        setMessage({ type: "error", text: "An error occurred. Please try again." });
                        setActionLoading(false);
                }
        };

        const handleUnlink = async () => {
                const token = localStorage.getItem("token");
                if (!token) {
                        navigate("/login");
                        return;
                }
                setActionLoading(true);
                setMessage({ type: "", text: "" });
                try {
                        const response = await fetch(`${IRACING_AUTH_API_BASE}/api/auth/iracing/unlink`, {
                                method: "POST",
                                headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                },
                        });
                        const data = await response.json();
                        if (response.ok) {
                                localStorage.setItem("iracingLinked", "false");
                                notifyIracingLinkChanged();
                                setIracingLinked(false);
                                setMessage({ type: "success", text: "Your iRacing account has been unlinked." });
                        } else {
                                setMessage({ type: "error", text: data.message || "Failed to unlink iRacing account." });
                        }
                } catch (err) {
                        console.error("unlink error:", err);
                        setMessage({ type: "error", text: "An error occurred. Please try again." });
                } finally {
                        setActionLoading(false);
                }
        };

        const handleMergeSubmit = async (e) => {
                e.preventDefault();
                const token = localStorage.getItem("token");
                if (!token) {
                        navigate("/login");
                        return;
                }
                if (!mergeUsername || !mergePassword) {
                        setMessage({ type: "error", text: "Please enter your existing email and password." });
                        return;
                }
                setActionLoading(true);
                setMessage({ type: "", text: "" });
                try {
                        const response = await fetch(`${IRACING_AUTH_API_BASE}/api/auth/iracing/merge-into-existing`, {
                                method: "POST",
                                headers: {
                                        Authorization: `Bearer ${token}`,
                                        "Content-Type": "application/json",
                                },
                                body: JSON.stringify({ username: mergeUsername, password: mergePassword }),
                        });
                        const data = await response.json();
                        if (response.ok) {
                                localStorage.setItem("token", data.token);
                                localStorage.setItem("username", data.username);
                                localStorage.setItem("isPaidUser", data.isPaidUser ? "true" : "false");
                                localStorage.setItem("iracingLinked", "true");
                                localStorage.setItem("iracingOnly", "false");
                                notifyIracingLinkChanged();
                                setUsername(data.username);
                                setIracingOnly(false);
                                setIracingLinked(true);
                                setShowMergeForm(false);
                                setMergeUsername("");
                                setMergePassword("");
                                loadConsent();
                                setMessage({
                                        type: "success",
                                        text: "Your iRacing account has been linked to your existing iRacingData account.",
                                });
                                if (typeof setIsLoggedIn === "function") {
                                        setIsLoggedIn(true);
                                }
                        } else {
                                setMessage({ type: "error", text: data.message || "Failed to link account." });
                        }
                } catch (err) {
                        console.error("merge-into-existing error:", err);
                        setMessage({ type: "error", text: "An error occurred. Please try again." });
                } finally {
                        setActionLoading(false);
                }
        };

        return (
                <div className="account-page">
                        <div className="account-card">
                                <h2>Account</h2>

                                <div className="account-row">
                                        <span className="account-label">
                                                {iracingOnly ? "iRacing Display Name" : "Email"}
                                        </span>
                                        <span className="account-value">{username || "—"}</span>
                                </div>

                                <div className="account-row">
                                        <span className="account-label">iRacing Account</span>
                                        <span className={`account-status ${iracingLinked ? "linked" : "not-linked"}`}>
                                                {iracingLinked ? "Linked" : "Not linked"}
                                        </span>
                                </div>

                                {iracingOnly && (
                                        <p className="account-note">
                                                You sign in with iRacing only. There is no email or password on this
                                                account.
                                        </p>
                                )}

                                {message.text && (
                                        <div className={`account-message ${message.type}`}>{message.text}</div>
                                )}

                                {iracingOnly ? (
                                        !showMergeForm ? (
                                                <button
                                                        type="button"
                                                        className="account-btn account-btn-primary"
                                                        onClick={() => {
                                                                setShowMergeForm(true);
                                                                setMessage({ type: "", text: "" });
                                                        }}
                                                        disabled={actionLoading}
                                                >
                                                        Link to existing iRacingData account
                                                </button>
                                        ) : (
                                                <div className="account-merge-wrapper">
                                                        <div className="account-merge-divider">
                                                                Link existing account
                                                        </div>
                                                        <form
                                                                className="account-merge-form"
                                                                onSubmit={handleMergeSubmit}
                                                        >
                                                                <input
                                                                        type="email"
                                                                        placeholder="Email"
                                                                        value={mergeUsername}
                                                                        onChange={(e) => setMergeUsername(e.target.value)}
                                                                        autoComplete="username"
                                                                />
                                                                <input
                                                                        type="password"
                                                                        placeholder="Password"
                                                                        value={mergePassword}
                                                                        onChange={(e) => setMergePassword(e.target.value)}
                                                                        autoComplete="current-password"
                                                                />
                                                                <button
                                                                        type="submit"
                                                                        className="account-btn account-btn-primary"
                                                                        disabled={actionLoading}
                                                                >
                                                                        {actionLoading ? "Linking…" : "Link Account"}
                                                                </button>
                                                        </form>
                                                        <button
                                                                type="button"
                                                                className="account-merge-cancel"
                                                                onClick={() => {
                                                                        setShowMergeForm(false);
                                                                        setMergeUsername("");
                                                                        setMergePassword("");
                                                                        setMessage({ type: "", text: "" });
                                                                }}
                                                                disabled={actionLoading}
                                                        >
                                                                Cancel
                                                        </button>
                                                </div>
                                        )
                                ) : iracingLinked ? (
                                        <button
                                                type="button"
                                                className="account-btn account-btn-secondary"
                                                onClick={handleUnlink}
                                                disabled={actionLoading}
                                        >
                                                {actionLoading ? "Working…" : "Unlink iRacing"}
                                        </button>
                                ) : (
                                        <button
                                                type="button"
                                                className="account-btn account-btn-primary"
                                                onClick={handleLink}
                                                disabled={actionLoading}
                                        >
                                                {actionLoading ? "Working…" : "Link iRacing Account"}
                                        </button>
                                )}

                                {consent && consent.linked && (
                                        <div className="account-consent">
                                                <div className="account-consent-header">
                                                        <span className="account-label">Show my name on iRacingData</span>
                                                        <span className={`account-status ${consent.consent ? "linked" : "not-linked"}`}>
                                                                {consent.consent ? "On" : "Off"}
                                                        </span>
                                                </div>
                                                <p className="account-consent-text">
                                                        When this is on, your iRacing display name
                                                        {consent.displayName ? ` (${consent.displayName})` : ""} and customer ID can appear
                                                        to anyone in race results, leaderboards, world records, telemetry, and contests, and
                                                        anyone can look up your career stats. When it is off, you appear as &ldquo;Hidden
                                                        Driver&rdquo; (you will still see your own name while signed in). You can turn this
                                                        off at any time.
                                                </p>
                                                {consent.consent ? (
                                                        <>
                                                                {consent.consentedAt && (
                                                                        <p className="account-note">
                                                                                Turned on {new Date(consent.consentedAt).toLocaleDateString()}
                                                                        </p>
                                                                )}
                                                                <button
                                                                        type="button"
                                                                        className="account-btn account-btn-secondary"
                                                                        onClick={() => updateConsent(false)}
                                                                        disabled={consentLoading}
                                                                >
                                                                        {consentLoading ? "Working…" : "Hide my name"}
                                                                </button>
                                                        </>
                                                ) : (
                                                        <>
                                                                <label className="account-consent-check">
                                                                        <input
                                                                                type="checkbox"
                                                                                checked={consentConfirm}
                                                                                onChange={(e) => setConsentConfirm(e.target.checked)}
                                                                        />
                                                                        I agree to iRacingData showing my iRacing display name and customer ID publicly.
                                                                </label>
                                                                <button
                                                                        type="button"
                                                                        className="account-btn account-btn-primary"
                                                                        onClick={() => updateConsent(true)}
                                                                        disabled={consentLoading || !consentConfirm}
                                                                >
                                                                        {consentLoading ? "Working…" : "Show my name"}
                                                                </button>
                                                        </>
                                                )}
                                        </div>
                                )}
                        </div>
                </div>
        );
};

export default AccountPage;
